/* eslint-disable react/function-component-definition */
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { getAPIUrl } from "../../../config/api";
import { UserRole } from "../../../constants/userRole";
import { useUserWithAccount } from "../../../context/UserAccountProvider";
import { parseDate } from "../../../helpers/date";
import {
  useGetAllBaseRateFilesQuery,
  useUploadBaseRateFileMutation,
  useDeleteBaseRateFileMutation,
  useGetSourceStatusQuery,
  useGetBaseRateFileRegenerationStatusQuery,
} from "../../../store/queries/baseRateFiles";
import { authSelector } from "../../../store/slices/auth";
import { userSelector } from "../../../store/slices/user";
import { IBaseRateFile } from "../../../types/baseRateFile";
import { BaseRateFileUpdateDialog } from "./BaseRateFileUpdateDialog";
import CreateBaseRateFileDialog from "./CreateBaseRateFileDialog";
import UploadSourceFilesDialog from "./UpoadSourceFilesDialog/UploadSourceFilesDialog";
import { useBaseRateFileKindConversion } from "./useBaseRateFileKindConversion";

/**
 * Date-only values for PrimeReact Calendar: local noon avoids UTC day-boundary bugs
 * (e.g. May 1 showing as April 30 when the component normalizes to UTC).
 */
function localDateForCalendar(
  y: number,
  monthIndex: number,
  day: number
): Date {
  return new Date(y, monthIndex, day, 12, 0, 0, 0);
}

/** Trimmed label; uniqueness mirrors API (per account list). */
function baseRateDisplayNameLabel(raw: string): string {
  return raw.trim();
}

/**
 * Row using the same display name as another logical file (different `fileName`).
 * Exclude rows for `excludeLogicalFileName` so terms of the same file can share a label.
 */
function baseRateDisplayNameColumnBody(rowData: IBaseRateFile) {
  return (
    <div className="flex flex-column align-items-start gap-1 w-full min-w-0 max-w-full">
      <span
        className="w-full overflow-hidden text-overflow-ellipsis white-space-nowrap"
        title={rowData.displayName}
      >
        {rowData.displayName}
      </span>
      {rowData.isCarveout ? (
        <Tag
          value="Carveout"
          severity="warning"
          rounded
          className="text-xs font-semibold"
        />
      ) : null}
    </div>
  );
}

function findConflictingBaseRateByDisplayName(
  rows: IBaseRateFile[],
  inputDisplayName: string,
  excludeLogicalFileName: string | undefined,
  isCarveout?: boolean
): IBaseRateFile | undefined {
  const label = baseRateDisplayNameLabel(inputDisplayName);
  if (!label) return undefined;
  const wantCarveout = isCarveout === true;
  return rows.find((f) => {
    if (
      excludeLogicalFileName != null &&
      f.fileName === excludeLogicalFileName
    ) {
      return false;
    }
    if ((f.isCarveout === true) !== wantCarveout) {
      return false;
    }
    return baseRateDisplayNameLabel(f.displayName) === label;
  });
}

/** Normalize Calendar onChange values to a stable local calendar day (noon). */
function calendarSelectionToLocalDay(
  value: Date | null | undefined
): Date | null {
  if (
    value == null ||
    !(value instanceof Date) ||
    Number.isNaN(value.getTime())
  ) {
    return null;
  }
  return localDateForCalendar(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  );
}

const StyledDialog = styled(Dialog)`
  .selected-files-list {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.5rem;
    margin: 1rem 0;
    max-height: 200px;
    overflow-y: auto;

    .selected-file-item {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid #f8f9fa;

      &:last-child {
        border-bottom: none;
      }

      .file-name {
        flex: 1;
        margin-right: 1rem;
      }

      .file-size {
        color: #6c757d;
        margin-right: 1rem;
        font-size: 0.875rem;
      }

      .remove-file-btn {
        background: transparent;
        border: none;
        color: #6c757d;
        width: 2rem;
        height: 2rem;
        padding: 0;

        &:hover {
          background: transparent;
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }

        .p-button-icon {
          font-size: 1rem;
        }
      }
    }
  }

  .file-upload-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }
`;

interface IBaseRateFilesTableProps {
  accountId?: string;
}

function toIsoDateOnly(value: Date | null): string | undefined {
  if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
    return undefined;
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayForCalendar(): Date {
  const n = new Date();
  return localDateForCalendar(n.getFullYear(), n.getMonth(), n.getDate());
}

const VALIDATION_PREFIX = "Base rate file validation failed:";

function parseBaseRateValidationErrors(
  message: string | undefined
): string[] | null {
  if (!message || !message.startsWith(VALIDATION_PREFIX)) return null;
  return message
    .slice(VALIDATION_PREFIX.length)
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

const BaseRateFilesTable: React.FC<IBaseRateFilesTableProps> = ({
  accountId,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);
  const { userInfo } = useUserWithAccount();
  // CLE-2892 — "Upload Source Files" (CMS source data) and "Create File" (generate a file from that
  // data) run entirely on SuperAdmin-only endpoints, and both read them on mount. This table is also
  // the Base Rate Files sub-tab HospitalStaff see, so for them the two controls are not rendered and
  // the source-status read is skipped. Uploading, updating and deleting a file stay available.
  const isSuperAdmin = userInfo?.scope === UserRole.SUPER_ADMIN;

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [updateDialogVisible, setUpdateDialogVisible] = useState(false);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [uploadSourceFilesDialogVisible, setUploadSourceFilesDialogVisible] =
    useState(false);
  const [selectedFileForUpdate, setSelectedFileForUpdate] =
    useState<IBaseRateFile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [uploadEffectiveDate, setUploadEffectiveDate] = useState<Date | null>(
    () => todayForCalendar()
  );
  const [uploadIsCarveout, setUploadIsCarveout] = useState(false);

  const [tableFirst, setTableFirst] = useState(0);
  const [tableRows, setTableRows] = useState(10);

  const { data: baseRateFilesData, isLoading } = useGetAllBaseRateFilesQuery({
    search: searchValue,
    accountId,
  });
  // Unfiltered list used for uniqueness validation so that an active table
  // search filter can't hide conflicting rows from the upload/update checks.
  const { data: allBaseRateFilesData } = useGetAllBaseRateFilesQuery({
    accountId,
  });
  const {
    data: sourceStatusData,
    isLoading: isSourceStatusLoading,
    refetch: refetchSourceStatus,
  } = useGetSourceStatusQuery(undefined, { skip: !isSuperAdmin });
  const [uploadFile] = useUploadBaseRateFileMutation();
  const [deleteFile] = useDeleteBaseRateFileMutation();

  const baseRateFiles = baseRateFilesData?.data || [];
  const allBaseRateFiles = allBaseRateFilesData?.data || [];

  const [displayedValue, setDisplayedValue] = useState<IBaseRateFile[]>([]);
  const [regenPollIntervalMs, setRegenPollIntervalMs] = useState(15000);

  const visibleRegenIds = useMemo(() => {
    const source = displayedValue.length > 0 ? displayedValue : baseRateFiles;
    return source
      .slice(tableFirst, tableFirst + tableRows)
      .map((f) => f.id)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
  }, [displayedValue, baseRateFiles, tableFirst, tableRows]);

  const visibleRegenIdsKey = visibleRegenIds.join(",");
  const visibleRegenIdsStable = useMemo(
    () =>
      visibleRegenIdsKey.length === 0 ? [] : visibleRegenIdsKey.split(","),
    [visibleRegenIdsKey]
  );

  const { data: regenStatusData, refetch: refetchRegenStatus } =
    useGetBaseRateFileRegenerationStatusQuery(
      { ids: visibleRegenIdsStable },
      {
        skip: visibleRegenIdsStable.length === 0,
        pollingInterval: regenPollIntervalMs,
      }
    );

  useEffect(() => {
    const m = regenStatusData?.data ?? {};
    const inProgress = Object.values(m).some((s) => s.inProgress);
    setRegenPollIntervalMs(inProgress ? 3000 : 15000);
  }, [regenStatusData]);

  /**
   * CLE-2567 — convert a file uploaded under the wrong kind. Attachment state for
   * the visible page is pre-fetched so the action can say up front that a file is
   * attached instead of failing after the user confirms.
   */
  const { openConvertDialog } = useBaseRateFileKindConversion({
    fileIds: visibleRegenIdsStable,
    accountId,
    showToast: (message) => toast.current?.show(message),
  });

  const uploadFilenameClash = useMemo(() => {
    if (!selectedFile) return undefined;
    return allBaseRateFiles.find(
      (f) =>
        f.fileName === selectedFile.name &&
        (f.isCarveout === true) === uploadIsCarveout
    );
  }, [selectedFile, allBaseRateFiles, uploadIsCarveout]);

  const uploadDisplayNameConflict = useMemo(() => {
    if (!uploadDialogVisible) return undefined;
    return findConflictingBaseRateByDisplayName(
      allBaseRateFiles,
      displayName,
      undefined,
      uploadIsCarveout
    );
  }, [uploadDialogVisible, allBaseRateFiles, displayName, uploadIsCarveout]);

  const openUpdateDialog = (file: IBaseRateFile) => {
    setSelectedFileForUpdate(file);
    setUpdateDialogVisible(true);
  };

  const handleUpload = async () => {
    if (!displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    if (!uploadEffectiveDate) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an effective date for this base rate term.",
      });
      return;
    }
    const uploadIso = toIsoDateOnly(uploadEffectiveDate);
    if (!uploadIso) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select an effective date for this base rate term.",
      });
      return;
    }

    const existingByFileName = allBaseRateFiles.find(
      (f) =>
        f.fileName === selectedFile.name &&
        (f.isCarveout === true) === uploadIsCarveout
    );
    if (existingByFileName) {
      toast.current?.show({
        severity: "warn",
        summary: "Use Update instead",
        detail:
          "A file with this name already exists for this type (base rate vs carveout). Open that file and use Update instead of a fresh upload.",
      });
      setUploadDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      setUploadEffectiveDate(todayForCalendar());
      setUploadIsCarveout(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      openUpdateDialog(existingByFileName);
      return;
    }

    const dupLabel = findConflictingBaseRateByDisplayName(
      allBaseRateFiles,
      displayName,
      undefined,
      uploadIsCarveout
    );
    if (dupLabel) {
      toast.current?.show({
        severity: "error",
        summary: "Display name already in use",
        detail: `Another base rate file in this account already uses this display name (file: ${dupLabel.fileName}). Choose a distinct label.`,
      });
      return;
    }

    setIsUploading(true);

    try {
      await uploadFile({
        file: selectedFile,
        displayName,
        accountId,
        effectiveDate: uploadIso,
        ...(uploadIsCarveout ? { isCarveout: true } : {}),
      }).unwrap();

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "File uploaded successfully",
      });

      setUploadDialogVisible(false);
      setSelectedFile(null);
      setDisplayName("");
      setUploadIsCarveout(false);
      setUploadEffectiveDate(todayForCalendar());
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: unknown) {
      const fetchErr = error as {
        data?: { message?: string; messages?: string[] };
      };
      const errText = fetchErr.data?.message ?? fetchErr.data?.messages?.[0];
      const parsed = parseBaseRateValidationErrors(errText);
      if (parsed) {
        setValidationErrors(parsed);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errText ?? "Failed to upload files",
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (file: IBaseRateFile) => {
    confirmDialog({
      message: `Are you sure you want to delete ${file.fileName}?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteFile(file.id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "File deleted successfully",
          });
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete file",
          });
        }
      },
    });
  };

  const handleDownload = async (file: IBaseRateFile) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${apiUrl.endsWith("/") ? "" : "/"}base-rate-files/${
          file.id
        }/download-url`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      window.open(data.url, "_blank");
    } catch (error: any) {
      console.error("Download error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to get download URL",
      });
    }
  };

  const renderRegenStatusBadge = (rowData: IBaseRateFile) => {
    const status = regenStatusData?.data?.[rowData.id];
    if (!status || status.total <= 0) return null;
    const failedCount = status.failedContractIds?.length ?? 0;
    if (status.inProgress) {
      return (
        <span
          className="inline-flex align-items-center gap-1 text-primary text-sm font-medium"
          title={`Regenerating attached contracts — ${status.completed} of ${status.total} done`}
        >
          <i className="pi pi-spin pi-spinner" />
          {`${status.completed}/${status.total}`}
        </span>
      );
    }
    if (failedCount === 0 && status.completed === status.total) {
      return (
        <span
          className="inline-flex align-items-center gap-1 text-green-600 text-sm font-medium"
          title="All attached contracts regenerated"
        >
          <i className="pi pi-check-circle" />
          {`${status.total}/${status.total}`}
        </span>
      );
    }
    const succeeded = status.total - failedCount;
    return (
      <span
        className="inline-flex align-items-center gap-1 text-orange-500 text-sm font-medium"
        title={`${succeeded} of ${status.total} regenerated; ${failedCount} failed — open the Contracts list to retry`}
      >
        <i className="pi pi-exclamation-triangle" />
        {`${succeeded}/${status.total}`}
      </span>
    );
  };

  const actionBodyTemplate = (rowData: IBaseRateFile) => {
    return (
      <div className="flex gap-2 align-items-center">
        {renderRegenStatusBadge(rowData)}
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleDownload(rowData)}
          tooltip="Download"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => openUpdateDialog(rowData)}
          tooltip="Update"
        />
        <Button
          icon={rowData.isCarveout ? "pi pi-file" : "pi pi-file-edit"}
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => openConvertDialog(rowData)}
          tooltip={
            rowData.isCarveout
              ? "Convert to base rate file"
              : "Convert to carve-out file"
          }
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const uploadDialog = (
    <StyledDialog
      header="Upload Base Rate or Carveout File"
      visible={uploadDialogVisible}
      onHide={() => {
        if (!isUploading) {
          setUploadDialogVisible(false);
          setDisplayName("");
          setSelectedFile(null);
          setUploadIsCarveout(false);
          setUploadEffectiveDate(todayForCalendar());
          setValidationErrors([]);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      }}
      style={{ width: "600px" }}
      closable={!isUploading}
      closeOnEscape={!isUploading}
    >
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="displayName">Display Name *</label>
          <InputText
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter a name for these base rate files"
          />
          {uploadDisplayNameConflict && (
            <Message
              severity="error"
              className="w-full mt-2"
              text={`Display name must be unique per account among logical files. Already used by “${uploadDisplayNameConflict.displayName}” (${uploadDisplayNameConflict.fileName}).`}
            />
          )}
        </div>
        <div className="field">
          <label htmlFor="uploadEffectiveDate">Term effective date *</label>
          <Calendar
            id="uploadEffectiveDate"
            value={uploadEffectiveDate}
            onChange={(e) =>
              setUploadEffectiveDate(
                calendarSelectionToLocalDay(e.value as Date | null)
              )
            }
            showIcon
            className="w-full"
            dateFormat="mm-dd-yy"
          />
        </div>
        <div className="field flex align-items-center gap-2">
          <Checkbox
            inputId="uploadIsCarveout"
            checked={uploadIsCarveout}
            onChange={(e) => setUploadIsCarveout(!!e.checked)}
          />
          <label htmlFor="uploadIsCarveout" className="m-0 cursor-pointer">
            Carveout file
          </label>
        </div>
        <p className="text-600 text-sm line-height-3 m-0">
          {uploadIsCarveout ? (
            <>
              Carveout files are not used until you attach them under{" "}
              <strong>Carve-Outs</strong> on a contract. Carveouts do not create
              new contract terms, only version updates are available here.
            </>
          ) : (
            <>
              New files are not on any contract until you attach them in
              contract rate calculations. Use <strong>Update</strong> on this
              file later to roll contracts forward, once they reference it.
            </>
          )}
        </p>
        <div className="field">
          <label>Select CSV file *</label>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setSelectedFile(e.target.files[0]);
                setValidationErrors([]);
                e.target.value = ""; // Reset input so same file can be selected again
              }
            }}
            style={{ display: "none" }}
            id="file-upload-input"
          />
          <Button
            label="Choose File"
            icon="pi pi-plus"
            className="p-button-outlined mb-3"
            onClick={() =>
              document.getElementById("file-upload-input")?.click()
            }
          />
          {selectedFile && (
            <>
              <div className="selected-files-list">
                <div className="selected-file-item">
                  <span className="file-name">{selectedFile.name}</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-plain remove-file-btn"
                    onClick={() => {
                      setSelectedFile(null);
                    }}
                  />
                </div>
              </div>
              {uploadFilenameClash && (
                <div className="mt-2">
                  <Message
                    severity="warn"
                    className="w-full"
                    text="A base rate file with this file name already exists. Use Update on that file (new term or new version) instead of a fresh upload."
                  />
                  <Button
                    type="button"
                    label="Open update for this file"
                    icon="pi pi-pencil"
                    className="p-button-outlined mt-2"
                    onClick={() => {
                      const row = uploadFilenameClash;
                      setUploadDialogVisible(false);
                      setSelectedFile(null);
                      setDisplayName("");
                      setUploadEffectiveDate(todayForCalendar());
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                      openUpdateDialog(row);
                    }}
                  />
                </div>
              )}
              {validationErrors.length > 0 && (
                <div className="mt-3 p-3 border-round surface-100 border-1 border-red-300">
                  <div className="flex align-items-center gap-2 mb-2 text-red-700 font-semibold">
                    <i className="pi pi-exclamation-triangle" />
                    File validation failed
                  </div>
                  <ul className="m-0 pl-4 text-sm line-height-3">
                    {validationErrors.map((err, i) => (
                      <li key={i} className="mb-1 text-red-700">
                        {err}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="file-upload-actions">
                <Button
                  label="Cancel"
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                />
                <Button
                  label="Upload"
                  icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
                  onClick={handleUpload}
                  disabled={
                    isUploading ||
                    !!uploadFilenameClash ||
                    !!uploadDisplayNameConflict
                  }
                  loading={isUploading}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </StyledDialog>
  );

  return (
    <div>
      <Toast ref={toast} />
      <div className="mb-3 flex justify-content-between">
        <div className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search base rate files..."
          />
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <>
              <Button
                label="Upload Source Files"
                icon="pi pi-cloud-upload"
                className="p-button-outlined"
                loading={isSourceStatusLoading}
                onClick={() => setUploadSourceFilesDialogVisible(true)}
              />
              <Button
                label="Create File"
                icon="pi pi-plus"
                className="p-button-outlined"
                onClick={() => setCreateDialogVisible(true)}
              />
            </>
          )}
          <Button
            label="Upload Files"
            icon="pi pi-upload"
            onClick={() => setUploadDialogVisible(true)}
          />
        </div>
      </div>
      <DataTable
        value={baseRateFiles || []}
        loading={isLoading}
        paginator
        rows={tableRows}
        first={tableFirst}
        onPage={(e) => {
          setTableFirst(e.first ?? 0);
          setTableRows(e.rows ?? 10);
        }}
        onValueChange={(rows) =>
          setDisplayedValue((rows ?? []) as IBaseRateFile[])
        }
        emptyMessage="No files uploaded yet."
        className="p-datatable-striped"
      >
        <Column
          field="displayName"
          header="Display Name"
          sortable
          body={baseRateDisplayNameColumnBody}
          style={{ maxWidth: "18rem" }}
        />
        <Column field="fileName" header="File Name" sortable />
        <Column
          header="Terms"
          body={(row: IBaseRateFile) => {
            // eslint-disable-next-line no-underscore-dangle
            const n = row._termCount;
            if (n == null || n <= 0) return "—";
            return `${n} term${n === 1 ? "" : "s"}`;
          }}
          style={{ width: "100px" }}
        />
        <Column
          field="effectiveYear"
          header="Year"
          sortable
          body={(row: IBaseRateFile) =>
            row.effectiveYear != null ? String(row.effectiveYear) : "—"
          }
          style={{ width: "90px" }}
        />
        <Column
          field="uploadedBy.name"
          header="Uploaded By"
          body={(rowData) => rowData.uploadedBy?.name}
        />
        <Column
          field="createdAt"
          header="Date Uploaded"
          sortable
          body={(rowData) => parseDate(rowData.createdAt)}
        />
        <Column
          header="Procedures"
          body={(rowData) => {
            const rowCount = rowData.metadata?.rowCount;
            return rowCount !== undefined
              ? `${rowCount.toLocaleString()} rows`
              : "-";
          }}
        />
        <Column field="version" header="Version" sortable />
        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ width: "260px" }}
        />
      </DataTable>
      {uploadDialog}
      <BaseRateFileUpdateDialog
        visible={updateDialogVisible}
        file={selectedFileForUpdate}
        accountId={accountId}
        onHide={() => {
          setUpdateDialogVisible(false);
          setSelectedFileForUpdate(null);
        }}
        onUpdated={() => refetchRegenStatus()}
      />
      {/* Mounted only for SuperAdmin: CreateBaseRateFileDialog reads override items on mount. */}
      {isSuperAdmin && (
        <>
          <CreateBaseRateFileDialog
            visible={createDialogVisible}
            onHide={() => setCreateDialogVisible(false)}
            sourceDataStatus={sourceStatusData?.data ?? null}
            existingBaseRateFiles={allBaseRateFiles}
          />
          <UploadSourceFilesDialog
            visible={uploadSourceFilesDialogVisible}
            onHide={() => setUploadSourceFilesDialogVisible(false)}
            sourceDataStatus={sourceStatusData?.data ?? null}
            onSuccess={() => {
              refetchSourceStatus();
            }}
          />
        </>
      )}
    </div>
  );
};

export default BaseRateFilesTable;
